import { useDonationFormContext } from '@web/components/page-components/DonationForm'
import {
  useNoOfInstallments,
  usePayByInstallment,
  useTotalAmount,
} from '@web/components/page-components/DonationForm/donationUtils'
import { roundToTwoDecimals } from '@web/components/utils/format'
import { DonationForm, DonationFrequency, PaymentMethodType } from '@web/graphql-components'
import { usePageStore } from 'apps/fe-donation/libs/hooks/usePageStore'
import { usePaymentStore } from 'apps/fe-donation/libs/hooks/usePaymentStore'
import { useCreatePaymentObjectMutation, useUpdatePaymentObjectMutation } from 'apps/fe-donation/libs/payment.queries'
import { prefetchThankYouPageLayout } from 'apps/fe-donation/libs/queries'
import { Decimal } from 'decimal.js'
import { useCallback, useState } from 'react'
import { v4 } from 'uuid'

export enum DonateActions {
  TO_DONATE_CREDIT_CARD,
}

export type DonationFormStepProps = DonationForm

export const BASE_PAYMENT_INTENT = {
  currency: 'aud',
}

export const TEXT_COLOR_TO_HEX = {
  dark: '#000',
  light: '#FFF',
}

export const getDonorPaysAmount = (amount: number): Decimal => {
  if (!amount) {
    return new Decimal(0)
  }
  const totalAmount = new Decimal(amount)

  const platformFees = usePaymentStore.getState().paymentSettings?.donorPaysPlatformFees
  const tax = usePaymentStore.getState().paymentSettings?.tax

  if (!platformFees || platformFees.length < 1) {
    return new Decimal(0)
  }

  for (const fee of platformFees) {
    if (fee?.feeRanges) {
      for (const range of fee.feeRanges) {
        if (
          totalAmount.greaterThanOrEqualTo(range.startRange ?? 0) &&
          (!range.endRange || totalAmount.lessThanOrEqualTo(range.endRange)) &&
          (range.percentageFee || range.fixFee)
        ) {
          const feePercentage = new Decimal(range.percentageFee ?? 0).dividedBy(100)
          const percentageFee = totalAmount.mul(feePercentage)
          const base = percentageFee.plus(new Decimal(range.fixFee ?? 0))

          const taxPercentage = new Decimal(tax?.percentage ?? 0).dividedBy(100)
          const taxFee = base.mul(taxPercentage)

          return base.plus(taxFee)
        }
      }
    }
  }
  return new Decimal(0)
}

export const useDonorPaysAmount = () => {
  const totalAmount = useDonationFormContext(({ formValues }) => formValues.amount ?? 0)
  const donorPaysAmount = getDonorPaysAmount(totalAmount).toDecimalPlaces(2, Decimal.ROUND_HALF_UP)

  return {
    donorPaysAmount: donorPaysAmount.mul(100).toNumber(),
    donorPaysAmountInDollars: donorPaysAmount.toNumber(),
  }
}

export const useTotalAmountWithDonorPays = (): number => {
  const formValues = useDonationFormContext(({ formValues }) => formValues)
  const totalAmount = formValues.amount ?? 0
  const amount = new Decimal(totalAmount)
  return formValues.isDonorPays
    ? amount.plus(getDonorPaysAmount(totalAmount)).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber()
    : amount.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber()
}

export const useInstallmentAmount = () => {
  const formValues = useDonationFormContext(({ formValues }) => formValues)
  const amount = formValues.amount ?? 0
  const isPayByInstallments = formValues.isPayByInstallments
  const { noOfInstallments } = usePayByInstallment()
  return isPayByInstallments && noOfInstallments ? roundToTwoDecimals(amount / (noOfInstallments[0] ?? 1)) : amount
}

export const EXCLUDED_FIELDS = ['', 'comment']

export const useHandlePaymentIntent = (onError: (error: string) => void) => {
  const formValues = useDonationFormContext((state) => state.formValues)
  const { extraFields, givingFrequency = DonationFrequency.OneTime, isDonorPays, isPayByInstallments } = formValues
  const { email } = extraFields ?? {}

  const prevAmount = usePaymentStore((state) => state.prevAmount)
  const prevPaymentMethodType = usePaymentStore((state) => state.paymentMethodType)
  const prevEmail = usePaymentStore((state) => state.prevEmail)
  const prevGivingFrequency = usePaymentStore((state) => state.prevGivingFrequency)
  const prevIsDonorPays = usePaymentStore((state) => state.prevIsDonorPays)
  const prevIsPayByInstallments = usePaymentStore((state) => state.prevIsPayByInstallments)
  const paymentActions = usePaymentStore((state) => state.actions)

  const { noOfInstallments } = usePayByInstallment()

  const paymentTransactionId = usePaymentStore((state) => state.paymentTransactionId)
  const paymentIntentId = usePaymentStore((state) => state.paymentIntentId)
  const subscriptionId = usePaymentStore((state) => state.subscriptionId)
  const paymentSubscriptionId = usePaymentStore((state) => state.paymentSubscriptionId)
  const { setPrevAmount, setPrevEmail, setPaymentMethodType, setPrevGivingFrequency, setPrevIsDonorPays } =
    paymentActions
  const totalAmount = Math.floor(useTotalAmount() * 100)
  const { donorPaysAmount } = useDonorPaysAmount()
  const { mutateAsync: createPaymentObject, isPending: isCreatingPaymentObject } = useCreatePaymentObjectMutation()
  const { mutateAsync: updatePaymentObject, isPending: isUpdatingPaymentObject } = useUpdatePaymentObjectMutation()

  const handlePaymentIntent = useCallback(
    async (paymentMethodType: PaymentMethodType) => {
      if (
        totalAmount === prevAmount &&
        prevPaymentMethodType === paymentMethodType &&
        email === prevEmail &&
        givingFrequency === prevGivingFrequency &&
        isDonorPays === prevIsDonorPays &&
        (isPayByInstallments === undefined || isPayByInstallments === prevIsPayByInstallments)
      ) {
        return true
      }

      try {
        if (
          !paymentIntentId ||
          !paymentTransactionId ||
          email != prevEmail ||
          ((givingFrequency === DonationFrequency.OneTime || prevGivingFrequency === DonationFrequency.OneTime) &&
            givingFrequency !== prevGivingFrequency) ||
          ((paymentMethodType === PaymentMethodType.DirectDebit ||
            prevPaymentMethodType === PaymentMethodType.DirectDebit) &&
            paymentMethodType !== prevPaymentMethodType)
        ) {
          const purchaseEventId = v4()
          usePageStore.setState({ purchaseEventId })
          // reset payment ids
          usePaymentStore.setState({
            paymentIntentId: '',
            paymentSubscriptionId: '',
            paymentTransactionId: '',
          })
          const res = await createPaymentObject({
            donationFormAmount: totalAmount,
            email,
            paymentMethodType,
            isDonorChooseToPayFee: isDonorPays,
            donorPaysAmount: isDonorPays ? donorPaysAmount : 0,
            givingFrequency,
            eventId: purchaseEventId,
            ...(isPayByInstallments
              ? {
                  installment: {
                    totalNumberOfInstallments: noOfInstallments![0],
                  },
                }
              : {}),
          })
          if (givingFrequency === DonationFrequency.OneTime) {
            usePaymentStore.setState((state) => ({
              ...state,
              paymentTransactionId: res.paymentTransactionId,
              clientSecret: res.clientSecret,
              paymentIntentId: res.paymentIntentId!,
            }))
          } else {
            usePaymentStore.setState((state) => ({
              ...state,
              paymentSubscriptionId: res.paymentSubscriptionId,
              clientSecret: res.clientSecret,
              subscriptionId: res.subscriptionId,
            }))
          }
          if (!usePageStore.getState().thankYouPageLayout) {
            prefetchThankYouPageLayout()
          }
        } else {
          const res = await updatePaymentObject({
            paymentIntentId,
            subscriptionId,
            donationFormAmount: totalAmount,
            paymentMethodType,
            email,
            givingFrequency,
            isDonorChooseToPayFee: isDonorPays,
            donorPaysAmount: isDonorPays ? donorPaysAmount : 0,
          })

          if (res.clientSecret) {
            usePaymentStore.setState((state) => ({
              ...state,
              clientSecret: res.clientSecret!,
            }))
          }
        }
      } catch {
        onError('Something went wrong. Please contact the charity and try again later')
        return false
      }

      onError('')
      if (totalAmount !== prevAmount) {
        setPrevAmount(totalAmount)
      }
      if (email != prevEmail) {
        setPrevEmail(email)
      }
      if (paymentMethodType !== prevPaymentMethodType) {
        setPaymentMethodType(paymentMethodType)
      }
      if (givingFrequency !== prevGivingFrequency) {
        setPrevGivingFrequency(givingFrequency)
      }
      if (isDonorPays !== prevIsDonorPays) {
        setPrevIsDonorPays(isDonorPays ?? false)
      }

      if (isPayByInstallments !== undefined && isPayByInstallments !== prevIsPayByInstallments) {
        usePaymentStore.setState({
          prevIsPayByInstallments: isPayByInstallments ?? false,
        })
      }

      return true
    },
    [
      totalAmount,
      email,
      givingFrequency,
      isDonorPays,
      prevPaymentMethodType,
      prevAmount,
      prevEmail,
      prevGivingFrequency,
      prevIsDonorPays,
      prevIsPayByInstallments,
      paymentIntentId,
      paymentTransactionId,
      noOfInstallments,
      isPayByInstallments,
      subscriptionId,
      paymentSubscriptionId,
    ],
  )

  return { handlePaymentIntent, isLoading: isCreatingPaymentObject || isUpdatingPaymentObject }
}
